import { ProductJourney } from "@shared/analytics/productEvents";
import {
    API_URLS,
    DEFAULT_QUERY_OPTIONS,
    type RetailWalletChallengeResponse,
    type YaasSavedDestination
} from "@solomon-apps/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryFunctionContext } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { observeSettingsVerification } from "@/services/analytics/settings";

import { useWalletVerification, type VerificationProof } from "./useWalletVerification";
import { requestWalletActionChallenge, submitWithVerificationRetry } from "./verificationRequests";

export type CreateSavedDestinationResult =
    | { success: true; data: YaasSavedDestination }
    | { success: false; error: string };

// Stable empty fallback. `query.data` is `undefined` until the fetch
// resolves (and stays undefined on error/empty), so `query.data ?? []` would
// hand back a fresh array reference every render — which, as a `useEffect`
// dependency in consumers (e.g. RetailYaasLandingPage's destinations sync),
// triggers setState every render and spins an infinite render loop. A shared
// module-level constant keeps the reference stable; React Query already
// returns a stable `data` reference once loaded, so the resolved case is
// stable too. Treated as immutable — consumers only read/map/spread it.
const EMPTY_DESTINATIONS: YaasSavedDestination[] = [];

export interface UseYaasSavedDestinationsResult {
    destinations: YaasSavedDestination[];
    isLoading: boolean;
    isCreating: boolean;
    error: Error | null;
    createDestination: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<CreateSavedDestinationResult>;
    refetch: () => void;
}

export function savedDestinationsQueryKey(
    address: string | null | undefined
): readonly [string, string, string | null | undefined] {
    return ["yaas", "saved-destinations", address] as const;
}

export function savedDestinationsUrl(apiBase: string, address: string): string {
    return `${apiBase}/api/yaas/wallet/${address}/saved-destinations`;
}

export function savedDestinationsChallengeUrl(apiBase: string, address: string): string {
    return `${savedDestinationsUrl(apiBase, address)}/challenge`;
}

function createChallengeBody(input: {
    name: string;
    address: string;
    analytics?: ProductJourney;
}): Record<string, unknown> {
    return {
        action: "create",
        name: input.name,
        address: input.address
    };
}

async function fetchSavedDestinations(address: string, signal?: AbortSignal): Promise<YaasSavedDestination[]> {
    const response = await fetch(savedDestinationsUrl(API_URLS.YAAS, address), {
        signal
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    // The list endpoint returns a paginated envelope ({ data, total, ... }),
    // not a bare array — unwrap to the destinations array.
    const body = (await response.json()) as { data?: YaasSavedDestination[] };
    return body.data ?? [];
}

async function requestSavedDestinationChallenge(
    address: string,
    input: { name: string; address: string; analytics?: ProductJourney }
): Promise<RetailWalletChallengeResponse> {
    return requestWalletActionChallenge(
        savedDestinationsChallengeUrl(API_URLS.YAAS, address),
        createChallengeBody(input),
        "Failed to request saved destination challenge"
    );
}

async function submitSavedDestinationCreate(
    address: string,
    input: { name: string; address: string; analytics?: ProductJourney },
    nonce: string,
    proof: VerificationProof,
    expiresAt: string
): Promise<YaasSavedDestination> {
    const response = await submitWithVerificationRetry(
        savedDestinationsUrl(API_URLS.YAAS, address),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                name: input.name,
                address: input.address,
                nonce,
                proof
            })
        },
        expiresAt
    );
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.message || "Failed to create saved destination");
    }
    return response.json() as Promise<YaasSavedDestination>;
}

export function useYaasSavedDestinations(walletAddress: string | null | undefined): UseYaasSavedDestinationsResult {
    const queryClient = useQueryClient();
    const walletVerification = useWalletVerification();
    const [isCreating, setIsCreating] = useState(false);

    const query = useQuery<YaasSavedDestination[], Error>({
        queryKey: savedDestinationsQueryKey(walletAddress),
        queryFn: (context: QueryFunctionContext): Promise<YaasSavedDestination[]> => {
            if (!walletAddress) {
                return Promise.reject(new Error("No address"));
            }
            return fetchSavedDestinations(walletAddress, context.signal);
        },
        enabled: !!walletAddress,
        ...DEFAULT_QUERY_OPTIONS
    });

    const { refetch: refetchQuery } = query;
    const refetch = useCallback((): void => {
        void refetchQuery();
    }, [refetchQuery]);

    const createDestination = async (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }): Promise<CreateSavedDestinationResult> => {
        if (!walletAddress) {
            return { success: false, error: "Wallet not connected" };
        }
        const analytics = input.analytics ?? new ProductJourney("saved_destination", "destination_book", walletAddress);
        const attempt = analytics.attempt({
            has_label: !!input.name.trim(),
            destination_kind: input.address === walletAddress ? "self" : "custom"
        });
        attempt.send("challenge_started");
        setIsCreating(true);
        try {
            const challenge = await requestSavedDestinationChallenge(walletAddress, input);
            attempt.challengeCompleted();
            const proof = await walletVerification.verify({
                purpose: "action",
                analytics: observeSettingsVerification(attempt),
                walletAddress,
                message: challenge.message,
                nonce: challenge.nonce,
                expiresAt: challenge.expiresAt,
                microTransfer: challenge.microTransfer
            });
            attempt.submit(proof.method);
            const data = await submitSavedDestinationCreate(
                walletAddress,
                input,
                challenge.nonce,
                proof,
                challenge.expiresAt
            );
            attempt.complete();
            queryClient.setQueryData<YaasSavedDestination[]>(savedDestinationsQueryKey(walletAddress), (prev) => {
                const current = prev ?? [];
                if (current.some((destination) => destination.uuid === data.uuid)) {
                    return current;
                }
                return [...current, data];
            });
            return { success: true, data };
        } catch (err: unknown) {
            attempt.fail(err);
            const message = err instanceof Error ? err.message : "Failed to create saved destination";
            return { success: false, error: message };
        } finally {
            setIsCreating(false);
        }
    };

    return {
        destinations: query.data ?? EMPTY_DESTINATIONS,
        isLoading: query.isLoading,
        isCreating,
        error: query.error,
        createDestination,
        refetch
    };
}
